// frontend/src/components/TareasSidebar.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './TareasSidebar.css';
import { useNotification } from '../context/NotificationContext';
import { useI18n } from '../i18n';
import { normalizeTaskPlace } from '../utils/taskPlaceNormalizer';

const TareasSidebar = ({ onNuevaTarea, onSelectTask, selectedTaskId, refreshFlag, onTaskMoved }) => {
  const { addNotification, addConfirm, addPrompt } = useNotification();
  const { t } = useI18n();
  const [history, setHistory] = useState([]);
  const [places, setPlaces] = useState([]);
  const [expandedPlaces, setExpandedPlaces] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null); // ID de la tarea con menú abierto
  const [showMoveModal, setShowMoveModal] = useState(null); // Tarea que se está moviendo

  const [movingTask, setMovingTask] = useState(false);

  const navigate = useNavigate();
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    if (activeMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [activeMenu]);

  const fetchPlaces = async () => {
    try {
      const res = await api.get('/places');
      if (res.data.ok) setPlaces((res.data.places || []).map(normalizeTaskPlace).filter(Boolean));
    } catch (err) {
      console.error("Error cargando lugares:", err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/tareas/history');
      if (res.data && res.data.history) {
        setHistory(res.data.history);
      }
    } catch (error) {
      console.error("Error cargando historial:", error);
      setHistory([]);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchPlaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFlag]);

  const handleClickTask = (id) => {
    if (typeof onSelectTask === 'function') onSelectTask(id);
    navigate(`/tareas/${encodeURIComponent(id)}`);
  };

  const handleDelete = async (e, id) => {
      e.stopPropagation();
      e.preventDefault();
      addConfirm(t('tasks.deleteConfirm'), async () => {
        try {
            await api.delete(`/tareas/${id}`);
            fetchHistory();
            if(String(selectedTaskId) === String(id)) {
                onSelectTask(null);
                navigate('/tareas');
            }
        } catch(err) {
            addNotification(t('tasks.deleteError'), "error");
        }
      });
  };

  const handleRename = async (e, id, currentTitle) => {
      e.stopPropagation();
      e.preventDefault();
      addPrompt(t('tasks.renamePrompt'), currentTitle, async (newTitle) => {
        if(!newTitle || newTitle === currentTitle) return;

        try {
            await api.put(`/tareas/${id}/rename`, { title: newTitle });
            fetchHistory();
            setActiveMenu(null);
        } catch(err) {
            addNotification(t('tasks.renameError'), "error");
        }
      });
  };

  const togglePin = async (e, task) => {
    e.stopPropagation();
    e.preventDefault();
    const meta = task.meta || {};
    const isPinned = !!meta.pinned;
    try {
      await api.put(`/tareas/${task.id}`, {
        meta: { ...meta, pinned: !isPinned }
      });
      fetchHistory();
      setActiveMenu(null);
    } catch (err) {
      addNotification(t('tasks.pinError'), "error");
    }
  };

  const moveToPlace = async (taskId, placeId) => {
    if (placeId === 'new') {
      navigate('/lugares', { state: { openCreateModal: true, pendingTaskId: taskId } });
      setShowMoveModal(null);
      setActiveMenu(null);
      return;
    }
    if (!taskId || !placeId || movingTask) return;
    try {
      setMovingTask(true);
      const selectedPlace = places.find(p => String(p.id) === String(placeId));
      const placeName = selectedPlace ? selectedPlace.name : t('nav.places');

      const res = await api.put(`/tareas/${taskId}`, {
        place_id: placeId
      });

      addNotification(t('tasks.movedToPlace', { place: placeName }), "success");

      if (typeof onTaskMoved === 'function' && res.data?.task) {
        onTaskMoved(res.data.task);
      }

      fetchHistory();
      setShowMoveModal(null);
      setActiveMenu(null);
    } catch (err) {
      addNotification(t('tasks.moveError'), "error");
    } finally {
      setMovingTask(false);
    }
  };

  const sortedHistory = [...history].sort((a, b) => {
    const aPinned = a.meta?.pinned ? 1 : 0;
    const bPinned = b.meta?.pinned ? 1 : 0;
    if (aPinned !== bPinned) return bPinned - aPinned;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <div className="tareas-sidebar">
      <div className="sidebar-header">
        <h2 className="sidebar-title">{t('common.tasks')}</h2>
      </div>

      <div className="sidebar-content overflow-y-auto">
        {/* Acciones principales */}
        <div className="sidebar-section-nav">
          <button className="nav-item-btn" onClick={() => {
              if (typeof onSelectTask === 'function') onSelectTask(null);
              if (typeof onNuevaTarea === 'function') onNuevaTarea();
              navigate('/tareas');
          }}>
            <span className="nav-icon">📄</span>
            <span className="nav-text">{t('tasks.newTask')}</span>
          </button>
        </div>

        {/* Sección Lugares */}
        <div className="sidebar-section">
          <div className="section-header-compact">
            <span>{t('nav.places')}</span>
            <div className="header-actions">
               <button className="icon-action-btn" onClick={() => navigate('/lugares', { state: { openCreateModal: true } })} title={t('tasks.newPlace')}>+</button>
               <button className="icon-action-btn" onClick={() => navigate('/lugares')} title={t('tasks.goPlaces')}>➔</button>
            </div>
          </div>
          <ul className="sidebar-list">
            {(expandedPlaces ? places : places.slice(0, 5)).map(place => (
              <li key={place.id} className="sidebar-list-item" onClick={() => navigate('/lugares')}>
                <span className="item-icon">🗺️</span>
                <span className="item-text truncate">{place.name}</span>
              </li>
            ))}
            {places.length > 5 && (
              <li className="sidebar-list-item toggle-item" onClick={() => setExpandedPlaces(!expandedPlaces)}>
                <span className="item-text">{expandedPlaces ? t('common.showLess') : t('common.showMore')}</span>
              </li>
            )}
            {places.length === 0 && <li className="empty-mini-text">{t('tasks.noPlaces')}</li>}
          </ul>
        </div>

        {/* Sección Historial */}
        <div className="sidebar-section">
          <div className="section-header-compact">
            <span>{t('tasks.history')}</span>
          </div>
          <ul className="sidebar-list">
            {sortedHistory.length === 0 && <li className="empty-mini-text">{t('tasks.noRecent')}</li>}
            {sortedHistory.map(task => (
              <li
                key={task.id}
                className={`sidebar-list-item task-item ${String(selectedTaskId) === String(task.id) ? 'active' : ''} ${task.meta?.pinned ? 'pinned' : ''}`}
              >
                <div className="item-main-content" onClick={() => handleClickTask(task.id)}>
                  <span className="item-icon">
                    {task.meta?.pinned ? '📌' : (task.completed ? '✅' : '📝')}
                  </span>
                  <div className="item-details">
                    <span className="item-text truncate">{task.main_task || t('tasks.noTitle')}</span>
                    {task.place_id && (
                      <span className="item-subtext truncate">
                        {places.find(p => String(p.id) === String(task.place_id))?.name || t('nav.places')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="item-actions" ref={activeMenu === task.id ? menuRef : null}>
                  <button className="kebab-trigger" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setActiveMenu(activeMenu === task.id ? null : task.id); }}>
                    ⋮
                  </button>
                  {activeMenu === task.id && (
                    <div className="kebab-dropdown-clean">
                        <button onClick={(e) => togglePin(e, task)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v2a2 2 0 0 0 1 1.73L11 16.27V21a1 1 0 0 0 2 0v-4.73l7-4.54A2 2 0 0 0 21 10z"></path></svg>
                            <span>{task.meta?.pinned ? t('tasks.unpin') : t('tasks.pin')}</span>
                        </button>
                        <button onClick={(e) => handleRename(e, task.id, task.main_task)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                            <span>{t('common.edit')}</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowMoveModal(task); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                            <span>{t('tasks.moveToPlace')}</span>
                        </button>
                        <button className="delete-action" onClick={(e) => handleDelete(e, task.id)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                            <span>{t('common.delete')}</span>
                        </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {showMoveModal && (
          <div className="modal-overlay-mini" onClick={() => setShowMoveModal(null)}>
              <div className="modal-content-mini move-place-modal" onClick={e => e.stopPropagation()}>
                  <h4>{t('tasks.moveToPlace')}</h4>
                  <p className="move-task-title">{showMoveModal.main_task || t('tasks.noTitle')}</p>
                  <div className="move-place-grid">
                      {places.map(place => {
                          const isCurrent = String(place.id) === String(showMoveModal.place_id);
                          return (
                              <button
                                  key={place.id}
                                  className={`move-place-card ${isCurrent ? 'current' : ''}`}
                                  onClick={() => moveToPlace(showMoveModal.id, place.id)}
                                  disabled={isCurrent || movingTask}
                              >
                                  <span>{place.name}</span>
                                  {place.ai_subtitle && <small>{place.ai_subtitle}</small>}
                                  <strong>{isCurrent ? t('places.alreadyInThisPlace') : t('tasks.moveToPlace')}</strong>
                              </button>
                          );
                      })}
                      {places.length === 0 && <p className="empty-msg">{t('tasks.noPlaces')}</p>}
                  </div>
                  <button className="move-place-create" onClick={() => moveToPlace(showMoveModal.id, 'new')} disabled={movingTask}>
                      + {t('tasks.createPlaceGo')}
                  </button>
                  <button className="move-place-close" onClick={() => setShowMoveModal(null)} disabled={movingTask}>{t('common.close')}</button>
              </div>
          </div>
      )}
    </div>
  );
};

export default TareasSidebar;
